/*
 * useMutation wrapper that shows an error using Chakra's Toast.
 */

import { useToast } from "@chakra-ui/react";
import { MutateOptions, UseMutationOptions, useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";

import { Errors, getErrorString, getErrors } from "../lib/utils/get-errors";

type ExtraOptions = {
  toastOnError?: boolean;
};

type OverrideOnError = {
  onError?: (errors: Errors) => void;
};

export type UseMutationWithErrorHandlerOptions<
  TData = unknown,
  TError = unknown,
  TVariables = void,
  TContext = unknown
> = Omit<UseMutationOptions<TData, TError, TVariables, TContext>, "onError"> &
  OverrideOnError &
  ExtraOptions;

export type MutationWithErrorHandlerOptions<
  TData = unknown,
  TError = unknown,
  TVariables = void,
  TContext = unknown
> = Omit<MutateOptions<TData, TError, TVariables, TContext>, "onError"> & OverrideOnError;

export function useMutationWithErrorHandler<
  TData = unknown,
  TError = unknown,
  TVariables = void,
  TContext = unknown
>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  options?: UseMutationWithErrorHandlerOptions<TData, TError, TVariables, TContext>
) {
  const toast = useToast();
  const intl = useIntl();

  const [errors, setErrors] = useState<Errors>(null);

  const { onError, toastOnError, ...restOptions } = options || {};

  const mutation = useMutation({
    mutationFn,
    ...restOptions,
  });

  // Clear errors when mutation is no longer in an error state
  useEffect(() => {
    if (!mutation.isError && errors) {
      setErrors(null);
    }
  }, [errors, mutation.isError]);

  const getWrappedMutate = <MutationKey extends "mutate" | "mutateAsync">(
    mutationKey: MutationKey
  ): ((
    variables: TVariables,
    mutateOptions?: MutationWithErrorHandlerOptions<TData, TError, TVariables, TContext>
  ) => MutationKey extends "mutate" ? void : Promise<TData>) => {
    return (variables, mutateOptions) => {
      const originalOnError = mutateOptions?.onError;

      const mutateFunc = mutation[mutationKey](variables, {
        ...mutateOptions,
        onError: async (error) => {
          const formattedErrors = await getErrors(error, intl);

          if (formattedErrors) {
            setErrors(formattedErrors);
          }

          if (onError) {
            onError(formattedErrors);
          }

          // Call per-call onError with raw error
          if (originalOnError) {
            originalOnError(formattedErrors);
          }

          if (toastOnError) {
            toast({
              title: "Error",
              status: "error",
              description: getErrorString(formattedErrors),
            });
          }
        },
      });

      /* tslint:disable */
      /* eslint-disable */
      return mutateFunc as any;
    };
  };

  const { isPending, ...rest } = mutation;
  return {
    ...rest,
    isMutating: isPending,
    mutate: getWrappedMutate("mutate"),
    mutateAsync: getWrappedMutate("mutateAsync"),
    error: getErrorString(errors),
    errors,
  } as const;
}
